import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  AccessTime,
  Quiz,
  ArrowBack,
  PlayArrow,
} from '@mui/icons-material';
import { getAssessmentDetail } from '../../utils/api';
import ChangeLanguage from '../sections/ChangeLanguage';

function AssessmentDetail() {
  const { assessmentId } = useParams();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get('taskId');
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assessmentData, setAssessmentData] = useState(null);
  
  // Helper function to get assessment topic based on current language
  const getAssessmentTopic = (assessment) => {
    const currentLang = i18n.language;
    if (currentLang === 'zh' || currentLang === 'zh-TW' || currentLang === 'zh-CN') {
      return assessment.topicZh || assessment.topic;
    }
    return assessment.topicEn || assessment.topic;
  };
  
  // Helper function to get assessment description based on current language
  const getAssessmentDescription = (assessment) => {
    const currentLang = i18n.language;
    if (currentLang === 'zh' || currentLang === 'zh-TW' || currentLang === 'zh-CN') {
      return assessment.descriptionZh || assessment.description;
    }
    return assessment.descriptionEn || assessment.description;
  };

  useEffect(() => {
    const fetchAssessmentDetail = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await getAssessmentDetail(assessmentId, taskId);
        setAssessmentData(data);
      } catch (error) {
        console.error('Error fetching assessment detail:', error);
        setError(t('AssessmentDetail.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    fetchAssessmentDetail();
  }, [assessmentId, taskId, t]);

  const handleBack = () => {
    navigate('/student-home');
  };

  const handleStartAssessment = () => {
    navigate(`/student/assessment/${assessmentId}/questions${taskId ? `?taskId=${taskId}` : ''}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>{t('AssessmentDetail.loadingDetails')}</Typography>
      </Container>
    );
  }

  if (error || !assessmentData) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || t('AssessmentDetail.assessmentNotFound')}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={handleBack}>
          {t('AssessmentDetail.backToHome')}
        </Button>
      </Container>
    );
  }

  const { assessment, questions, task } = assessmentData;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa', py: 4 }}>
      <Container maxWidth="md">
        {/* Header with Back Button and Language Switcher */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{ 
              color: '#5FB88A',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                bgcolor: 'rgba(126, 205, 163, 0.08)',
              },
            }}
          >
            {t('Login.backToHome')}
          </Button>
          <ChangeLanguage buttonColor="#5FB88A" />
        </Box>

        {/* Main Content */}
        <Paper 
          sx={{ 
            borderRadius: 3, 
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid #e0e0e0',
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #4e7ba8 0%, #65cb9a 100%)',
              color: 'white',
              p: 4,
            }}
          >            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>              {getAssessmentTopic(assessment)}            </Typography>            
            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
              <Chip
                icon={<Quiz sx={{ color: 'white !important' }} />}
                label={`${questions.length} ${t('AssessmentDetail.questions')}`}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
              {assessment.status && (
                <Chip
                  label={assessment.status}
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
              )}
            </Box>
          </Box>

          {/* Details Section */}
          <Box sx={{ p: 4 }}>
            {/* Description */}
            {(assessment.description || assessment.descriptionEn || assessment.descriptionZh) && (
              <>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('AssessmentDetail.description')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: 'text.secondary' }}>
                  {getAssessmentDescription(assessment)}
                </Typography>
                <Divider sx={{ my: 3 }} />
              </>
            )}

            {/* Task Info (for assigned assessments) */}
            {task && (
              <>
                {task.startDate && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <AccessTime sx={{ color: '#4e7ba8' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {t('AssessmentDetail.assignedDate')}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: 'text.secondary', ml: 4 }}>
                      {formatDate(task.startDate)}
                    </Typography>
                  </Box>
                )}
                
                {task.expiredDate && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <AccessTime sx={{ color: '#d32f2f' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#d32f2f' }}>
                        {t('AssessmentDetail.dueDate')}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: '#d32f2f', ml: 4 }}>
                      {formatDate(task.expiredDate)}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* Created At (for global assessments) */}
            {!task && assessment.createdAt && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <AccessTime sx={{ color: '#4e7ba8' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {t('AssessmentDetail.created')}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ color: 'text.secondary', ml: 4 }}>
                  {formatDate(assessment.createdAt)}
                </Typography>
              </Box>
            )}

            {/* Start Button */}
            <Button
              variant="contained"
              fullWidth
              size="large"
              endIcon={<PlayArrow />}
              onClick={handleStartAssessment}
              sx={{
                mt: 4,
                py: 1.5,
                background: 'linear-gradient(135deg, #7ECDA3 0%, #5FB88A 100%)',
                color: 'white',
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(126, 205, 163, 0.4)',
                transition: 'all 0.3s',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5FB88A 0%, #4A9D7A 100%)',
                  boxShadow: '0 6px 16px rgba(126, 205, 163, 0.5)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {t('AssessmentDetail.startAssessment')}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default AssessmentDetail;
